import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogIn, UserPlus, Eye, EyeOff, AlertCircle, Sun, Moon, Share, Plus, ChevronDown, ChevronUp, Smartphone } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useTheme } from "../contexts/ThemeContext";

const API = (window.location.origin.includes('localhost') 
  ? process.env.REACT_APP_BACKEND_URL 
  : window.location.origin) + "/api";

const Login = ({ onLogin }) => {
  const { isDark, toggleTheme } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);
  
  // Password change modal state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [tempUser, setTempUser] = useState(null);
  
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let response;
      if (isLogin) {
        response = await axios.post(`${API}/auth/login`, { email, password });
      } else {
        response = await axios.post(`${API}/auth/register`, { 
          email, 
          password, 
          name,
          role: "salesperson"
        });
      }

      const { access_token, user } = response.data;
      
      // Check if user needs to change password
      if (user.must_change_password) {
        setTempToken(access_token);
        setTempUser(user);
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(user));
      onLogin(user);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError("");
    
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      
      // Update user to remove the flag
      const updatedUser = { ...tempUser, must_change_password: false };
      localStorage.setItem("token", tempToken);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      onLogin(updatedUser);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Password change modal
  if (showPasswordChange) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 transition-colors ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_sales-scorecards/artifacts/8h0qwbfz_calldrip-logo-white%20%282%29.png" 
                alt="Calldrip" 
                className={`h-10 w-auto ${!isDark ? 'brightness-0' : ''}`}
              />
              <span className={`text-[10px] tracking-[0.2em] uppercase mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>scorecards</span>
            </div>
          </div>

          <div className={`rounded-2xl p-6 sm:p-8 shadow-xl transition-colors ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
            <h2 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Set Your New Password
            </h2>
            <p className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Welcome, {tempUser?.name}! Please create a new password to continue.
            </p>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400">
                <AlertCircle size={18} />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : "text-slate-700"}>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`pr-10 focus:border-indigo-500 ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : "text-slate-700"}>Confirm Password</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`focus:border-indigo-500 ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5"
                disabled={loading}
                data-testid="set-password-button"
              >
                {loading ? (
                  <div className="loading-spinner w-5 h-5 border-t-white"></div>
                ) : (
                  "Set Password & Continue"
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-colors ${isDark ? 'bg-slate-900' : 'bg-slate-100'}`}>
      {/* Theme toggle in top right */}
      <button
        onClick={toggleTheme}
        className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200'}`}
        data-testid="login-theme-toggle"
      >
        {isDark ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex flex-col items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_sales-scorecards/artifacts/8h0qwbfz_calldrip-logo-white%20%282%29.png" 
              alt="Calldrip" 
              className={`h-10 w-auto ${!isDark ? 'brightness-0' : ''}`}
            />
            <span className={`text-[10px] tracking-[0.2em] uppercase mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>scorecards</span>
          </div>
          <p className={`mt-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Performance Tracker</p>
        </div>

        {/* Login/Register Card */}
        <div className={`rounded-2xl p-6 sm:p-8 shadow-xl transition-colors ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`}>
          <h2 className={`text-xl font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
            {isLogin ? "Welcome back" : "Create account"}
          </h2>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="space-y-2">
                <Label className={isDark ? "text-slate-300" : "text-slate-700"}>Full Name</Label>
                <Input
                  type="text"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`focus:border-indigo-500 ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : "text-slate-700"}>Email</Label>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`focus:border-indigo-500 ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                required
                autoComplete="username"
                name="email"
                id="email"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : "text-slate-700"}>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pr-10 focus:border-indigo-500 ${isDark ? 'bg-slate-900 border-slate-600 text-white placeholder:text-slate-500' : 'bg-slate-50 border-slate-300 text-slate-900 placeholder:text-slate-400'}`}
                  required
                  autoComplete="current-password"
                  name="password"
                  id="password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? (
                <div className="loading-spinner w-5 h-5 border-t-white"></div>
              ) : isLogin ? (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create Account
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError("");
              }}
              className={`text-sm transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
              data-testid="toggle-auth-mode"
            >
              {isLogin ? (
                <>Don't have an account? <span className="text-indigo-500">Sign up</span></>
              ) : (
                <>Already have an account? <span className="text-indigo-500">Sign in</span></>
              )}
            </button>
            
            {isLogin && (
              <Link 
                to="/forgot-password"
                className={`block text-sm transition-colors ${isDark ? 'text-slate-400 hover:text-indigo-400' : 'text-slate-500 hover:text-indigo-600'}`}
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </Link>
            )}
          </div>
        </div>

        {/* Add to Home Screen Instructions */}
        <div className={`mt-4 rounded-xl transition-colors ${isDark ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white/50 border border-slate-200'}`}>
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className={`w-full p-4 flex items-center justify-between ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
            data-testid="add-to-home-toggle"
          >
            <div className="flex items-center gap-2">
              <Smartphone size={18} className="text-indigo-500" />
              <span className="text-sm font-medium">Add to Home Screen</span>
            </div>
            {showInstructions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {showInstructions && (
            <div className={`px-4 pb-4 space-y-4 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {/* iPhone Instructions */}
              <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-slate-100'}`}>
                <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <span className="text-lg">📱</span> iPhone / iPad
                </h4>
                <ol className="text-xs space-y-1.5 list-decimal list-inside">
                  <li>Tap the <strong>Share</strong> button <Share size={12} className="inline mx-1" /> at the bottom of Safari</li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong> <Plus size={12} className="inline mx-1" /></li>
                  <li>Tap <strong>"Add"</strong> in the top right corner</li>
                </ol>
              </div>

              {/* Android Instructions */}
              <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-slate-100'}`}>
                <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <span className="text-lg">🤖</span> Android
                </h4>
                <ol className="text-xs space-y-1.5 list-decimal list-inside">
                  <li>Tap the <strong>three dots menu</strong> (⋮) in Chrome</li>
                  <li>Tap <strong>"Add to Home screen"</strong></li>
                  <li>Tap <strong>"Add"</strong> to confirm</li>
                </ol>
              </div>

              <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                This will create an app icon on your home screen for quick access!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
